import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  TextField, IconButton, Button, Switch,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import dayjs from 'dayjs';
import TipTapEditor from '../../../Widgets/TipTapEditor/TipTapEditor';
import {
  createEmptyNote,
  parseClientNotes,
  serializeClientNotes,
  noteHasContent,
  isIndividualNote,
  getNotePrimaryText,
  touchNote,
} from './clientNotesUtils';
import Styles from './ClientNotesEditor.module.scss';

const initNotes = (value, editable) => {
  const parsed = parseClientNotes(value);
  if (parsed.length > 0) return parsed;
  return editable ? [createEmptyNote()] : [];
};

const initialExpandedId = (initialNotes, editable) => {
  if (editable && initialNotes.length === 1 && !noteHasContent(initialNotes[0])) {
    return initialNotes[0].id;
  }
  return null;
};

const formatNoteDate = (iso) => {
  if (!iso) return null;
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : null;
};

const isEmptyBody = (html) => {
  if (!html || typeof html !== 'string') return true;
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim() === '';
};

const ClientNotesEditor = ({
  value,
  onChange,
  direction = 'rtl',
  t,
  editable = true,
  compact = false,
  /**
   * Read-only display mode (ClientDataTable):
   * - every saved note is shown as a card, regardless of isIndividual
   * - the note title/content stays hidden until the user clicks ShowNote
   * - individual notes get a lock indicator
   * isIndividual only changes presentation, never whether the card exists.
   */
  maskUntilShown = false,
}) => {
  const lastEmittedRef = useRef(value || '');
  const initialNotesRef = useRef(null);
  if (initialNotesRef.current === null) {
    initialNotesRef.current = initNotes(value, editable);
  }
  const [notes, setNotes] = useState(initialNotesRef.current);
  const [expandedId, setExpandedId] = useState(() =>
    initialExpandedId(initialNotesRef.current, editable));
  const [shownIds, setShownIds] = useState(() => new Set());

  useEffect(() => {
    if ((value || '') === (lastEmittedRef.current || '')) return;
    lastEmittedRef.current = value || '';
    const next = initNotes(value, editable);
    setNotes(next);
    setExpandedId((current) => {
      if (current && next.some((note) => note.id === current)) return current;
      return initialExpandedId(next, editable);
    });
    setShownIds(new Set());
  }, [value, editable]);

  const emitChange = (nextNotes) => {
    setNotes(nextNotes);
    const serialized = serializeClientNotes(nextNotes);
    lastEmittedRef.current = serialized;
    if (onChange) onChange(serialized);
  };

  const patchNote = (id, patch) => {
    // Keep the note open. The empty draft used to collapse as soon as the
    // first character made it "have content", which unmounted the title field.
    setExpandedId(id);
    emitChange(notes.map((note) => (note.id === id ? touchNote(note, patch) : note)));
  };

  const handleTitleChange = (id, title) => patchNote(id, { title });
  const handleBodyChange = (id, body) => patchNote(id, { body });
  const handleIndividualChange = (id, isIndividual) => patchNote(id, { isIndividual });

  const handleAddNote = () => {
    const next = createEmptyNote('', { isIndividual: false });
    emitChange([...notes, next]);
    setExpandedId(next.id);
  };

  const handleRemoveNote = (id) => {
    const next = notes.filter((note) => note.id !== id);
    emitChange(next.length > 0 ? next : (editable ? [createEmptyNote()] : []));
    setExpandedId(null);
  };

  const toggleShown = (id) => {
    setShownIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const noteDate = (note) => formatNoteDate(note.updatedAt) || formatNoteDate(note.createdAt);

  const renderIndividualBadge = () => (
    <span className={Styles.individualBadge}>
      <LockOutlinedIcon sx={{ fontSize: 'var(--xtram-header-btn-font, 13px)' }} />
      {t('IndividualNoteShort')}
    </span>
  );

  const renderIndividualSwitch = (note) => (
    <label className={Styles.individualControl}>
      <span className={Styles.individualControlLabel}>{t('IndividualNoteShort')}</span>
      <Switch
        size="small"
        checked={Boolean(note.isIndividual)}
        onChange={(e) => handleIndividualChange(note.id, e.target.checked)}
        color="primary"
      />
    </label>
  );

  // ---------------------------------------------------------------------------
  // Read-only display: card exists for BOTH normal and individual notes.
  // Title/content are masked until ShowNote is clicked.
  // ---------------------------------------------------------------------------
  if (!editable) {
    const visible = notes.filter(noteHasContent);
    if (visible.length === 0) return null;

    return (
      <div className={`${Styles.editor} ${compact ? '' : Styles.editorWide}`} dir={direction}>
        <div className={`${Styles.list}${compact ? ` ${Styles.listSingle}` : ''}`}>
          {visible.map((note) => {
            const individual = isIndividualNote(note);
            // Only individual notes are masked; normal notes are visible by default.
            const masked = maskUntilShown && individual;
            const shown = !masked || shownIds.has(note.id);
            const dateLabel = noteDate(note);

            return (
              <div key={note.id} className={Styles.savedCard}>
                {/* RTL flex: first child = right → ○ | label | badge | date | ShowNote */}
                <div className={Styles.savedRow}>
                  <span className={Styles.bullet} aria-hidden="true" />
                  <span className={shown ? Styles.savedTitle : Styles.savedTitleMasked}>
                    {shown ? (getNotePrimaryText(note) || t('Notes')) : t('NoteExists')}
                  </span>
                  {individual && renderIndividualBadge()}
                  {dateLabel && <span className={Styles.savedDate}>{dateLabel}</span>}
                  {masked && (
                    <Button
                      size="small"
                      startIcon={shown ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      onClick={(e) => { e.stopPropagation(); toggleShown(note.id); }}
                      className={Styles.showBtn}
                    >
                      {shown ? t('HideNote') : t('ShowNote')}
                    </Button>
                  )}
                </div>
                {shown && !isEmptyBody(note.body) && (
                  <div className={Styles.savedBody} onClick={(e) => e.stopPropagation()}>
                    <TipTapEditor
                      value={note.body || ''}
                      onChange={() => {}}
                      editable={false}
                      compact
                      direction={direction}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Editable mode: full editor. Individual notes are shown here too so they can
  // be created / edited. isIndividual is a per-note switch, not a visibility gate.
  // ---------------------------------------------------------------------------
  return (
    <div className={`${Styles.editor} ${compact ? '' : Styles.editorWide}`} dir={direction}>
      <div className={`${Styles.list}${compact ? ` ${Styles.listSingle}` : ''}`}>
        {notes.map((note, index) => {
          const isExpanded = expandedId === note.id
            || (!expandedId && index === notes.length - 1 && !noteHasContent(note));
          const primary = getNotePrimaryText(note);
          const dateLabel = noteDate(note);
          const individual = isIndividualNote(note);

          if (!isExpanded && noteHasContent(note)) {
            // RTL flex: first = right → ○ | title | badge | date | 🗑
            return (
              <div key={note.id} className={Styles.savedRow}>
                <span className={Styles.bullet} aria-hidden="true" />
                <button
                  type="button"
                  className={Styles.savedTitleBtn}
                  onClick={() => setExpandedId(note.id)}
                >
                  {primary || t('Notes')}
                </button>
                {individual && renderIndividualBadge()}
                {dateLabel && <span className={Styles.savedDate}>{dateLabel}</span>}
                <IconButton
                  size="small"
                  onClick={() => handleRemoveNote(note.id)}
                  aria-label={t('DeleteNote')}
                  className={Styles.rowIconBtn}
                  disabled={notes.length === 1 && !noteHasContent(note)}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </div>
            );
          }

          return (
            <div key={note.id} className={Styles.editorCard}>
              {/* RTL flex: first = right → ○ | אישי | title | 🗑 */}
              <div className={Styles.editorHeader}>
                <span className={Styles.bullet} aria-hidden="true" />

                {renderIndividualSwitch(note)}

                <TextField
                  className={Styles.titleInput}
                  fullWidth
                  size="small"
                  variant="standard"
                  placeholder={t('NoteTitle')}
                  value={note.title}
                  onChange={(e) => handleTitleChange(note.id, e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: 'var(--xtram-header-btn-font, 13px)',
                      fontWeight: 700,
                      color: '#142d58',
                      px: 1,
                      py: 0.35,
                      borderRadius: 1,
                      background: '#fff',
                      border: '1px solid #e5ebf3',
                      '& input': { textAlign: direction === 'ltr' ? 'left' : 'right' },
                      '&:focus-within': { borderColor: '#cad7ee' },
                    },
                  }}
                  inputProps={{
                    dir: direction,
                    'aria-label': `${t('NoteTitle')} ${index + 1}`,
                  }}
                />

                <IconButton
                  size="small"
                  onClick={() => handleRemoveNote(note.id)}
                  aria-label={t('DeleteNote')}
                  className={Styles.rowIconBtn}
                  disabled={notes.length === 1 && !noteHasContent(note)}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </div>

              <div className={Styles.bodyWrap}>
                <TipTapEditor
                  value={note.body || ''}
                  onChange={(body) => handleBodyChange(note.id, body)}
                  placeholder={t('NoteBody')}
                  direction={direction}
                />
              </div>

              {dateLabel && (
                <div className={Styles.editorFooterDate}>{dateLabel}</div>
              )}
            </div>
          );
        })}
      </div>

      <Button
        size="small"
        startIcon={<AddOutlinedIcon />}
        onClick={handleAddNote}
        className={Styles.addBtn}
      >
        {t('AddNote')}
      </Button>
    </div>
  );
};

ClientNotesEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  direction: PropTypes.string,
  t: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  compact: PropTypes.bool,
  maskUntilShown: PropTypes.bool,
};

export default ClientNotesEditor;
